import { RefresherEventDetail } from '@ionic/core';
import {
  IonBadge,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonRefresher,
  IonRefresherContent,
  useIonToast,
} from '@ionic/react';
import {
  chevronForward,
  downloadOutline,
  gitCompareOutline,
} from 'ionicons/icons';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '../../api/authorization.provider';
import {
  GetScratchpadDocument,
  ListScratchpadHistoriesInput,
  PlanEntitlementKey,
  Scratchpad,
  ScratchpadHistory,
  useGetScratchpadHistoryQuery,
  useGetUserQuery,
  useListScratchpadHistoriesQuery,
  useRestoreScratchpadHistoryMutation,
} from '../../api/graphql/generated';
import {
  trackScratchpadHistoryCompareVersions,
  trackScratchpadHistoryRestore,
  trackViewScratchpadHistory,
} from '../../config/analytics';
import { useIsMachineActive } from '../../pages/machine/machine-context-provider';
import { sleep } from '../../util/async';
import { formatDate } from '../../util/date-utils';
import { getUsername } from '../../util/user-utils';
import { BaseButton } from '../button/base-button';
import { LoadingButton } from '../button/loading-button';
import { WithConfirmation } from '../button/with-confirmation';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { IonItem } from '../ionic/IonItem';
import { useScratchpadServiceContext } from '../machine/scratchpad/scratchpad-service-context-provider';
import { PaywallViewMore } from '../paywall/paywall-view-more';
import { Collapsible } from '../ui/collapsible';
import { DateField } from '../ui/date-field';
import {
  ScratchpadHistoryCompareModal,
  ScratchpadHistoryCompareModalProps,
} from './scratchpad-history-compare-modal';
import './scratchpad.css';

interface ScratchpadHistoryComponentProps {
  closePresentingElement?: () => void;
}

const ScratchpadHistoryComponent = (
  props: ScratchpadHistoryComponentProps
): ReactElement => {
  const { closePresentingElement } = props;
  const { i18n } = useTranslation();

  const infiniteScrollRef = useRef<HTMLIonInfiniteScrollElement>(null);
  const { scratchpad } = useScratchpadServiceContext();

  useEffect(() => {
    if (scratchpad.app.slug) {
      trackViewScratchpadHistory(scratchpad.app.slug);
    }
  }, [scratchpad.app.slug]);

  const limit = 50;

  const listScratchpadHistoriesInput: ListScratchpadHistoriesInput = useMemo(
    () => ({
      scratchpadId: scratchpad.id,
      limit,
      offset: 0,
    }),
    [scratchpad.id]
  );

  const { data, loading, refetch, error, fetchMore } =
    useListScratchpadHistoriesQuery({
      variables: {
        input: listScratchpadHistoriesInput,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    });

  const totalItems = data?.listScratchpadHistories.total ?? 0;
  const itemsLength = data?.listScratchpadHistories.items?.length ?? 0;

  const hasAllItems = totalItems === itemsLength;

  const getMore = useCallback(async () => {
    await fetchMore({
      variables: {
        input: {
          ...listScratchpadHistoriesInput,
          offset: itemsLength,
        },
      },
    });
    infiniteScrollRef.current?.complete();
  }, [fetchMore, itemsLength, listScratchpadHistoriesInput]);

  const [restore] = useRestoreScratchpadHistoryMutation();
  const [presentToast, _dismissToast] = useIonToast();
  const [selectedItem, setSelectedItem] =
    useState<Partial<ScratchpadHistory | undefined>>();

  const historyItems: Partial<ScratchpadHistory>[] = useMemo(
    () => data?.listScratchpadHistories.items ?? [],
    [data?.listScratchpadHistories.items]
  );

  const appSlug = scratchpad?.app?.slug;

  const doRestore = useCallback(
    async (item: Partial<ScratchpadHistory>) => {
      if (item.scratchpadId) {
        const success = await restore({
          variables: {
            input: {
              scratchpadHistoryId: item.id as string,
              scratchpadId: item.scratchpadId,
            },
          },
          refetchQueries: [
            {
              query: GetScratchpadDocument,
              variables: {
                input: { id: item.scratchpadId },
              },
            },
          ],
        });
        if (success) {
          presentToast({
            message: `${i18n.t('common:scratchpad_restore_success_message', {
              date: formatDate(item.updatedAt),
            })}`,
            color: 'success',
            position: 'top',
            duration: 2000,
          });
          trackScratchpadHistoryRestore(appSlug);
          if (closePresentingElement) {
            closePresentingElement();
          }
        }
      }
    },
    [restore, presentToast, appSlug, closePresentingElement, i18n]
  );

  const doRefresh = useCallback(
    async (event: CustomEvent<RefresherEventDetail>) => {
      await Promise.all([
        refetch({
          input: { scratchpadId: scratchpad.id, offset: 0, limit },
        }),
        sleep(500),
      ]);
      event.detail.complete();
    },
    [refetch, scratchpad.id]
  );

  const historyCompareModalProps = useMemo(():
    | ScratchpadHistoryCompareModalProps
    | undefined => {
    if (
      selectedItem?.id &&
      selectedItem?.scratchpadId &&
      historyItems[0]?.id !== undefined
    ) {
      const selectedIndex = historyItems?.findIndex(
        (i) => i.id === selectedItem.id
      );

      return {
        scratchpadId: selectedItem?.scratchpadId,
        selectedHistoryItemId: selectedItem.id,

        // because of sort order desc our next item will be the previous index and our prev item
        // will be the next index in the array
        nextHistoryItemId: historyItems[selectedIndex - 1]?.id,
      };
    }

    return;
  }, [selectedItem?.id, selectedItem?.scratchpadId, historyItems]);

  const parentModalRef = document.getElementById('scratchpad-history-modal');

  const onSelect = async (item: Partial<ScratchpadHistory>) => {
    setSelectedItem(item);
  };

  return (
    <>
      <LoadingRetry
        loading={loading}
        error={error}
        refetch={refetch}
        results={{
          hasResults: true,
          noResultsComponent: (
            <div className="content-center ion-padding">
              {i18n.t('common:scratchpad_no_history_found')}
            </div>
          ),
        }}
      >
        <IonList className="scratchpad-history-list">
          <ScratchpadInfo scratchpad={scratchpad} />
          {historyItems.length > 0 && (
            <IonListHeader>
              {i18n.t('common:scratchpad_version_prior_versions')}
            </IonListHeader>
          )}
          {historyItems.map((item, index) => {
            return (
              <ScratchpadHistoryItem
                key={index}
                scratchpad={scratchpad}
                item={item}
                onRestore={doRestore}
                onSelect={onSelect}
              />
            );
          })}
          <IonInfiniteScroll
            ref={infiniteScrollRef}
            onIonInfinite={getMore}
            className="ion-padding"
            disabled={totalItems === itemsLength}
            threshold={'25%'}
          >
            <IonInfiniteScrollContent loadingText={i18n.t('common:loading')} />
          </IonInfiniteScroll>
          {hasAllItems && (
            <div className="end-history-container">
              <PaywallViewMore
                planEntitlementKey={
                  PlanEntitlementKey.SCRATCHPAD_HISTORY_MAX_AGE
                }
              >
                {i18n.t('common:scratchpad_end_of_version_history')}
              </PaywallViewMore>
            </div>
          )}
        </IonList>
      </LoadingRetry>
      <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
        <IonRefresherContent />
      </IonRefresher>
      <IonModal
        isOpen={selectedItem !== undefined}
        presentingElement={parentModalRef ?? undefined}
        canDismiss={true}
        backdropDismiss={false}
        onDidPresent={() => {
          trackScratchpadHistoryCompareVersions(selectedItem?.app?.slug);
        }}
        onDidDismiss={() => {
          if (selectedItem) {
            setSelectedItem(undefined);
          }
        }}
      >
        {historyCompareModalProps && (
          <ScratchpadHistoryCompareModal
            dismiss={() => setSelectedItem(undefined)}
            {...historyCompareModalProps}
          />
        )}
      </IonModal>
    </>
  );
};

interface ScratchpadInfoProps {
  scratchpad: Scratchpad;
}

const ScratchpadInfo = (props: ScratchpadInfoProps): ReactElement => {
  const { scratchpad } = props;
  const { i18n } = useTranslation();

  const { data: userData } = useGetUserQuery({
    variables: { input: { id: scratchpad.updatedBy as string } },
    skip: typeof scratchpad.updatedBy !== 'string',
  });

  const { data: latestScratchpadHistory } = useGetScratchpadHistoryQuery({
    variables: { input: { scratchpadId: scratchpad.id } },
    fetchPolicy: 'cache-and-network',
  });

  const updatedByLabel = getUsername(userData?.getUser);

  const versionExecutionCount =
    latestScratchpadHistory?.getScratchpadHistory.versionExecutionCount ?? 0;

  const exeuctionCount =
    versionExecutionCount > 0 ? versionExecutionCount : undefined;

  return (
    <div className="scratchpad-latest-version">
      <IonListHeader>
        {i18n.t('common:scratchpad_version_latest_version')}
      </IonListHeader>
      <IonItem lines="none">
        <IonLabel>
          <h2>
            <DateField date={scratchpad.updatedAt} />
          </h2>
          {updatedByLabel && (
            <p>
              {i18n.t('common:last_updated_by')} {updatedByLabel}
            </p>
          )}
        </IonLabel>
        <IonBadge>{exeuctionCount}</IonBadge>
      </IonItem>
    </div>
  );
};

interface ScratchpadHistoryItemProps {
  scratchpad: Scratchpad;
  item: Partial<ScratchpadHistory>;
  onSelect: (item: Partial<ScratchpadHistory>) => Promise<void>;
  onRestore: (item: Partial<ScratchpadHistory>) => Promise<void>;
}

const ScratchpadHistoryItem = (
  props: ScratchpadHistoryItemProps
): ReactElement => {
  const { item, onRestore, onSelect, scratchpad } = props;
  const { i18n } = useTranslation();
  const isMachineActive = useIsMachineActive();

  const [isExpanded, setExpanded] = useState<boolean>(false);

  const canUpdateScratchpad = useHasPermission(
    'update',
    'scratchpad',
    scratchpad
  );

  const { data: updatedByData } = useGetUserQuery({
    variables: {
      input: {
        id: item.updatedBy as string,
      },
    },
    fetchPolicy: 'cache-first',
    skip: typeof item.updatedBy !== 'string',
  });

  const { data: createdByData } = useGetUserQuery({
    variables: {
      input: {
        id: item.createdBy as string,
      },
    },
    fetchPolicy: 'cache-first',
    skip: typeof item.createdBy !== 'string',
  });

  const classList: string[] = [];

  if (isExpanded) {
    classList.push('expanded-history-item');
  }

  const exeuctionCount =
    (item.versionExecutionCount ?? 0) > 0
      ? item.versionExecutionCount
      : undefined;

  const updatedByUser = getUsername(updatedByData?.getUser);
  const createdByUser = getUsername(createdByData?.getUser);

  const itemSublabel = updatedByUser
    ? `${i18n.t('common:last_updated_by')} ${updatedByUser}`
    : `${i18n.t('common:created_by')} ${createdByUser}`;

  return (
    <>
      <IonItem
        className={classList.join(' ')}
        lines="full"
        button={true}
        detail={false}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setExpanded(!isExpanded);
        }}
      >
        <IonLabel>
          <h2>
            <DateField date={item.scratchpadUpdatedAt} />
          </h2>
          {itemSublabel && <p>{itemSublabel}</p>}
        </IonLabel>
        <IonBadge>{exeuctionCount}</IonBadge>
        <IonIcon
          className="expansion-control"
          icon={chevronForward}
          size="small"
          color="medium"
        />
      </IonItem>
      <Collapsible collapsed={!isExpanded}>
        <div className="scratchpad-history-collapsible-container">
          <ScratchpadVersionDetails item={item} />
          <div className="spacer" />
          <div className="scratchpad-history-action-wrapper">
            <div className="scratchpad-history-action-container">
              <BaseButton
                buttonProps={{
                  onClick: () => onSelect(item),
                }}
              >
                <IonIcon slot="start" icon={gitCompareOutline} />
                {i18n.t('common:scratchpad_version_compare_button')}
              </BaseButton>
              {canUpdateScratchpad && isMachineActive && (
                <WithConfirmation
                  header={i18n.t(
                    'common:scratchpad_restore_this_version_confirmation'
                  )}
                >
                  {({ onConfirmed, waitingForConfirmation }) => {
                    return (
                      <LoadingButton
                        loading={waitingForConfirmation}
                        buttonProps={{
                          color: 'primary',
                          fill: 'solid',
                          onClick: () =>
                            onConfirmed({
                              onConfirmation: () => onRestore(item),
                            }),
                        }}
                      >
                        <IonIcon slot="start" icon={downloadOutline} />
                        {i18n.t(
                          'common:scratchpad_restore_this_version_prompt'
                        )}
                      </LoadingButton>
                    );
                  }}
                </WithConfirmation>
              )}
            </div>
          </div>
        </div>
      </Collapsible>
    </>
  );
};

interface ScratchpadVersionDetailsProps {
  item: Partial<ScratchpadHistory> | null;
}

const ScratchpadVersionDetails = (
  props: ScratchpadVersionDetailsProps
): ReactElement | null => {
  const { item } = props;
  const { i18n } = useTranslation();
  if (item) {
    const lastExecuted = item?.versionExecutedAt ? (
      <DateField date={item?.versionExecutedAt} />
    ) : (
      <>{i18n.t('common:scratchpad_version_never_executed')}</>
    );

    const versionExecutionCount = item?.versionExecutionCount ?? 0;
    return (
      <div className="scratchpad-version-details">
        <div className="scratchpad-version-executed-label">
          {i18n.t('common:scratchpad_version_last_executed')}
        </div>
        <div className="scratchpad-version-executed-value">{lastExecuted}</div>
        <div className="scratchpad-version-executed-count-label">
          {i18n.t('common:scratchpad_version_execution_count')}
        </div>
        <div className="scratchpad-version-executed-count-value">
          {versionExecutionCount}
        </div>
      </div>
    );
  } else {
    return null;
  }
};

export { ScratchpadHistoryComponent };
